import React from 'react';
import { ArrowDownToLineIcon } from '@revfluence/fresh-icons/regular/esm';
import { ChevronLeftIcon, ChevronRightIcon } from '@revfluence/fresh-icons/regular/esm';
import { TableColumnsIcon } from '@revfluence/fresh-icons/solid/esm';
import SearchInput from '@frontend/app/refresh-components/SearchInput';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import {
  TableHeader,
  TableHeaderLayout,
  TableHeaderSection,
  TableHeaderLabel,
  TableHeaderIconButton,
} from '../../../Analytics/components/table-header/TableHeader';

interface AdsTableHeaderProps {
  totalCount: number;
  currentPage: number;
  pageSize: number;
  startRecord: number;
  endRecord: number;
  onSearchChange?: (value: string) => void;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
  searchValue?: string;
  onExport?: (type: 'current' | 'full') => void;
  isExporting?: boolean;
  isRefreshing?: boolean;
  className?: string;
  onOpenColumnDialog?: () => void;
}

export const AdsTableHeader: React.FC<AdsTableHeaderProps> = ({
  totalCount,
  currentPage,
  pageSize: _pageSize,
  startRecord,
  endRecord,
  onSearchChange,
  onPreviousPage,
  onNextPage,
  searchValue = '',
  onExport,
  isExporting = false,
  isRefreshing = false,
  className,
  onOpenColumnDialog,
}) => {
  const hasPreviousPage = currentPage > 1;
  const hasNextPage = endRecord < totalCount;

  const handleExport = (type: 'current' | 'full') => {
    onExport?.(type);
  };

  return (
    <TableHeader className={className}>
      <TableHeaderLayout>
        <TableHeaderSection>
          <TableHeaderLabel>
            <div className="flex items-center gap-4">
              <span aria-live="polite">
                {isRefreshing ? 'Refreshing...' : `${totalCount} ad${totalCount !== 1 ? 's' : ''}`}
              </span>
            </div>
          </TableHeaderLabel>
        </TableHeaderSection>

        <TableHeaderSection>
          {/* Search Input */}
          {onSearchChange && (
            <SearchInput
              searchText={searchValue}
              setSearchText={onSearchChange}
              buttonProps={{ 'data-dd-action-name': 'social-ads-search-toggle' }}
            />
          )}

          {/* Edit Columns Button */}
          {onOpenColumnDialog && (
            <TableHeaderIconButton
              onClick={onOpenColumnDialog}
              data-dd-action-name="ads-table-edit-columns"
              aria-label="Edit table columns"
              className="h-4 w-4"
            >
              <TableColumnsIcon className="h-4 w-4" aria-hidden="true" />
            </TableHeaderIconButton>
          )}

          {/* Export Dropdown */}
          {onExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <TableHeaderIconButton
                  disabled={isExporting || totalCount === 0}
                  data-dd-action-name="export-ads-data"
                  aria-label={isExporting ? 'Export in progress...' : 'Export ads data'}
                  aria-busy={isExporting}
                >
                  <div className="flex items-center">
                    <ArrowDownToLineIcon className="h-4 w-4" aria-hidden="true" />
                  </div>
                </TableHeaderIconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => handleExport('current')}
                  disabled={isExporting}
                  data-dd-action-name="export-current-view"
                  aria-label="Export current view of ads data"
                  className="cursor-pointer"
                >
                  Export current view
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleExport('full')}
                  disabled={isExporting}
                  data-dd-action-name="export-full-data"
                  aria-label="Export all ads data fields"
                  className="cursor-pointer"
                >
                  Export all fields
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Record Range Display */}
          {(!isRefreshing && (
            <span
              className="text-sm text-gray-600"
              aria-label={`Showing ${startRecord} to ${endRecord} of ${totalCount} ads`}
            >
              {startRecord} - {endRecord} of {totalCount}
            </span>
          )) || <div className="h-4 rounded bg-gray-100 animate-pulse w-16" />}

          {/* Pagination Navigation */}
          <nav aria-label="Table navigation" className="flex items-center">
            <TableHeaderIconButton
              onClick={onPreviousPage}
              disabled={!hasPreviousPage}
              data-dd-action-name="ads-table-previous-page"
              aria-label="Previous page"
              aria-disabled={!hasPreviousPage}
            >
              <ChevronLeftIcon aria-hidden="true" />
            </TableHeaderIconButton>

            <TableHeaderIconButton
              onClick={onNextPage}
              disabled={!hasNextPage}
              data-dd-action-name="ads-table-next-page"
              aria-label="Next page"
              aria-disabled={!hasNextPage}
            >
              <ChevronRightIcon aria-hidden="true" />
            </TableHeaderIconButton>
          </nav>
        </TableHeaderSection>
      </TableHeaderLayout>
    </TableHeader>
  );
};
